import { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import debounce from 'lodash.debounce'
import { NewsletterApi } from '../../api/newsletter'
import LanguageContext from '../../contexts/languageContext'
import * as Validator from '../../helpers/validator'
import { OTHER_PROFILE } from './constants'

const useNewsletter = () => {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [profile, setProfile] = useState('')
  const [otherProfile, setOtherProfile] = useState('')
  const [isInvalidEmail, setIsInvalidEmail] = useState(true)
  const api = useMemo(() => new NewsletterApi(), [])
  const onChangeEmail = useCallback(({ target: { value } }) => setEmail(value), [])
  const onChangeName = useCallback(({ target: { value } }) => setName(value), [])
  const onChangeProfile = target =>  setProfile(target.value)
  const onChangeOtherProfile = useCallback(({ target: { value } }) => setOtherProfile(value), [])

  const onValidationEmail = useMemo(
    () =>
      debounce(email => {
        const isInvalidEmailMsg = Validator.isInvalidEmail(email)
        setIsInvalidEmail(isInvalidEmailMsg)
      }, 300),
    []
  )

  useEffect(() => {
    onValidationEmail(email)
  }, [email, onValidationEmail])

  return {
    api,
    email,
    name,
    profile,
    otherProfile,
    setEmail,
    setProfile,
    onChangeEmail,
    onChangeName,
    onChangeProfile,
    onChangeOtherProfile,
    isInvalidEmail,
  }
}

const useNewsletterForm = (api, email, name, profile, otherProfile, setEmail, setProfile) => {
  const { lang: locale } = useContext(LanguageContext)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccessfullyRegistered, setIsSuccessfullyRegistered] = useState(false)
  const [hasEmailRegistered, setHasEmailRegistered] = useState(false)
  const [hasServerError, setHasServerError] = useState(false)

  useEffect(() => {
    if (!isSubmitted) {
      setIsSuccessfullyRegistered(false)
      setHasEmailRegistered(false)
      setHasServerError(false)
      setEmail('')
      setProfile('')
    }
  }, [isSubmitted, setEmail, setProfile])

  const onSubmit = useCallback(
    event => {
      event.preventDefault()
      setIsSubmitted(true)
      setIsLoading(true)

      if (otherProfile && profile === OTHER_PROFILE) {
        profile = `${profile} - ${otherProfile}`
      }

      if (!Validator.isInvalidEmail(email)) {
        api
          .sendEmail(email, name, locale, profile)
          .then(() => setIsSuccessfullyRegistered(true))
          .catch(({ response }) => {
            const isEmailRegisteredError = response && response.status && response.status === 400

            setHasEmailRegistered(isEmailRegisteredError)
            setHasServerError(!isEmailRegisteredError)
          })
          .finally(() => setIsLoading(false))
      } else {
        setIsLoading(false)
      }
    },
    [api, email, name, locale, profile, otherProfile]
  )
  return { onSubmit, isLoading, isSubmitted, setIsSubmitted, isSuccessfullyRegistered, hasEmailRegistered, hasServerError }
}

export { useNewsletter, useNewsletterForm }
